import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ShoppingBag, Check, ZoomIn } from "lucide-react";
import { useCart } from "../lib/cart";
import { FILE_BASE } from "../lib/api";

/**
 * Full-screen lightbox for viewing watermarked previews zoomed in.
 * Supports keyboard navigation (←/→/Esc), pinch-to-zoom on mobile (native),
 * and add-to-cart from inside the modal.
 */
export default function PhotoLightbox({ photos, index, onClose, onIndexChange }) {
  const { has, add, remove } = useCart();
  const [zoomed, setZoomed] = useState(false);

  const photo = photos[index];

  const goPrev = useCallback(() => {
    setZoomed(false);
    onIndexChange((index - 1 + photos.length) % photos.length);
  }, [index, photos.length, onIndexChange]);

  const goNext = useCallback(() => {
    setZoomed(false);
    onIndexChange((index + 1) % photos.length);
  }, [index, photos.length, onIndexChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [goPrev, goNext, onClose]);

  if (!photo) return null;

  const inCart = has(photo.id);
  const numbers = photo.tags_overridden
    ? (photo.manual_numbers || [])
    : [...new Set([...(photo.detected_numbers || []), ...(photo.manual_numbers || [])])];

  const toggleCart = () => {
    if (inCart) remove(photo.id);
    else add(photo);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex flex-col"
      data-testid="lightbox"
      onClick={onClose}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between p-4 text-white text-sm border-b border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-white/50 tabular-nums whitespace-nowrap">
            {index + 1} / {photos.length}
          </span>
          <span className="text-white/30">·</span>
          <span className="truncate">{photo.event_name}</span>
          {photo.photographer_name && (
            <>
              <span className="text-white/30">·</span>
              <span className="text-white/60 truncate">{photo.photographer_name}</span>
            </>
          )}
          {numbers.length > 0 && (
            <>
              <span className="text-white/30">·</span>
              <div className="flex gap-1">
                {numbers.slice(0, 3).map((n) => (
                  <span key={n} className="bg-brand text-white text-xs font-bold uppercase px-1.5 py-0.5">
                    #{n}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/10 transition-colors flex-shrink-0"
          aria-label="Stäng"
          data-testid="lightbox-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Main image area */}
      <div
        className="flex-1 relative flex items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Prev / next buttons (desktop) */}
        {photos.length > 1 && (
          <>
            <button
              onClick={goPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/60 hover:bg-brand text-white flex items-center justify-center z-10 transition-colors"
              aria-label="Föregående"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/60 hover:bg-brand text-white flex items-center justify-center z-10 transition-colors"
              aria-label="Nästa"
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        <div
          className="relative max-w-full max-h-full overflow-auto cursor-zoom-in"
          onClick={() => setZoomed((z) => !z)}
          data-testid="lightbox-image-wrap"
        >
          <img
            src={`${FILE_BASE}${photo.preview_url}`}
            alt={photo.event_name || "Foto"}
            className={`block transition-transform duration-200 ${
              zoomed ? "scale-[2] cursor-zoom-out" : "max-w-[95vw] max-h-[80vh] cursor-zoom-in"
            }`}
            data-testid="lightbox-image"
          />
        </div>

        {!zoomed && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white/70 text-xs uppercase tracking-wider px-3 py-1.5 flex items-center gap-2 pointer-events-none">
            <ZoomIn className="w-3.5 h-3.5" /> Klicka för att zooma · ←/→ för att bläddra · Esc stänger
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div
        className="flex items-center justify-between gap-3 p-4 border-t border-white/10 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-display font-bold text-2xl text-brand">
          {photo.price_sek?.toFixed(0)} kr
        </div>
        <button
          onClick={toggleCart}
          className={`${
            inCart ? "bg-green-600 hover:bg-green-700" : "bg-brand hover:bg-brand-hover"
          } text-white font-display font-bold uppercase tracking-wider px-6 py-3 transition-colors flex items-center gap-2`}
          data-testid="lightbox-cart-toggle"
        >
          {inCart ? (
            <>
              <Check className="w-4 h-4" /> I varukorg
            </>
          ) : (
            <>
              <ShoppingBag className="w-4 h-4" /> Lägg i varukorg
            </>
          )}
        </button>
      </div>
    </div>
  );
}
